import { PickerColadas } from '@/components/dominio/PickerColadas';
import { PickerObras } from '@/components/dominio/PickerObras';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useActividades, useCrearMuestra } from '@/hooks/useLaboratorio';
import { nombrePlantaDisplay } from '@/lib/plantas';
import { cn } from '@/lib/utils';
import {
  type CategoriaConcreto,
  type ColadaListadoItem,
  type CrearMuestraRequest,
  PLANTAS_LAB,
} from '@adelante/shared';
import { Building2, Check, Plus, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

/**
 * Modal de creación de muestra de laboratorio.
 *
 * Flujo (orden importante para el laboratorista):
 *  1. Elegir la colada de la planta (PickerColadas). Al seleccionar
 *     autocompleta fecha, obra y tipo de concreto.
 *  2. Si la planta no está en el sistema (Fiori u otras externas),
 *     se puede saltar la colada y llenar manual.
 *  3. Actividad (catálogo) — obligatorio.
 *  4. F'C objetivo (botones rápidos).
 *  5. Edades a probar (default 7/14/28). Al crear, el backend pre-crea
 *     los ensayos vacíos para que la tarjeta de detalle quede lista para
 *     cargar MPa cuando falle cada cilindro.
 */

const FC_COMUNES = [140, 175, 210, 245, 280, 315, 350, 420] as const;
const EDADES_TIPICAS = [7, 14, 28] as const;
const EDADES_EXTRA = [3, 56, 90, 180] as const;

interface Props {
  abierto: boolean;
  cerrar: () => void;
  /** Callback opcional invocado con el id de la muestra recién creada. */
  onCreado?: (idMuestra: number) => void;
}

/**
 * Intenta deducir un F'C numérico desde la descripción de la receta BC.
 * Ejemplos: "210 LOSA" → 210, "SELLO 280" → 280, "BLEND-1 ROJA" → null.
 * Devolvemos null si no encontramos un número plausible (100-600).
 */
function deducirFcDeReceta(descripcion: string | null): number | null {
  if (!descripcion) return null;
  const matches = descripcion.match(/\b(\d{2,4})\b/g);
  if (!matches) return null;
  for (const m of matches) {
    const n = Number(m);
    if (n >= 100 && n <= 600) return n;
  }
  return null;
}

/**
 * Detecta la categoría del concreto desde la descripción de la receta BC.
 *   "CONCRETO FC 210 KG/CM2 AUTOCOMPACTABLE" → 'autocompactable'
 *   "CONCRETO FC 210 KG/CM2 NORMAL"          → 'convencional'
 *   sin pista                                  → 'convencional' (default
 *     más común en Adelante; el operador puede sobrescribir).
 */
function deducirCategoriaDeReceta(descripcion: string | null): CategoriaConcreto {
  if (!descripcion) return 'convencional';
  const upper = descripcion.toUpperCase();
  if (upper.includes('AUTOCOMPACTABLE') || upper.includes('AUTOCOMPACT')) {
    return 'autocompactable';
  }
  return 'convencional';
}

export function ModalCrearMuestra(props: Props) {
  const actividades = useActividades(false);
  const crear = useCrearMuestra();

  // Estado interno. La obra principal viene de la colada; el operador
  // también puede asignar manualmente (sin colada) o sobrescribir.
  const [idColada, setIdColada] = useState<number | null>(null);
  const [coladaSel, setColadaSel] = useState<ColadaListadoItem | null>(null);
  const [obraManual, setObraManual] = useState<string | null>(null);
  const [plantaNombre, setPlantaNombre] = useState('');
  const [idActividad, setIdActividad] = useState<number | null>(null);
  const [fechaColado, setFechaColado] = useState(() => new Date().toISOString().slice(0, 10));
  const [fcObjetivo, setFcObjetivo] = useState<number>(210);
  const [categoria, setCategoria] = useState<CategoriaConcreto>('convencional');
  const [tipoLibre, setTipoLibre] = useState('');
  const [notas, setNotas] = useState('');
  // Por default NINGUNA edad pre-seleccionada — el operador elige cuál(es).
  // Antes seleccionábamos 7/14/28 pero pidió que sea selección explícita.
  const [edadesSel, setEdadesSel] = useState<number[]>([]);
  const [edadExtra, setEdadExtra] = useState('');
  const [mostrarManual, setMostrarManual] = useState(false);

  // Reset al abrir
  useEffect(() => {
    if (props.abierto) {
      setIdColada(null);
      setColadaSel(null);
      setObraManual(null);
      setPlantaNombre('');
      setIdActividad(null);
      setFechaColado(new Date().toISOString().slice(0, 10));
      setFcObjetivo(210);
      setCategoria('convencional');
      setTipoLibre('');
      setNotas('');
      setEdadesSel([]);
      setEdadExtra('');
      setMostrarManual(false);
    }
  }, [props.abierto]);

  // Cuando elige una colada, autocompletar campos derivados
  useEffect(() => {
    if (!coladaSel) return;
    // Fecha del colado = fecha_inicio de la colada (truncamos a YYYY-MM-DD)
    setFechaColado(coladaSel.fecha_inicio.slice(0, 10));
    // Planta: la colada trae el código técnico (BLEND-1/BLEND-2), lo
    // traducimos al nombre de equipo (Roja E50/Bianca E50) para que coincida
    // con PLANTAS_LAB. El operador puede sobrescribirlo si hace falta.
    setPlantaNombre(nombrePlantaDisplay(coladaSel.planta_nombre));
    const descripcion = coladaSel.descripcion_receta_bc ?? coladaSel.receta_blend_nombre;
    // Tipo de concreto = descripción de la receta BC si existe, sino la receta blend
    setTipoLibre(descripcion);
    // F'C deducido
    const fc = deducirFcDeReceta(descripcion);
    if (fc !== null) setFcObjetivo(fc);
    // Categoría deducida (autocompactable si la receta lo indica explícito).
    setCategoria(deducirCategoriaDeReceta(descripcion));
  }, [coladaSel]);

  // Obra final = la de la colada (si seleccionada) o la manual (sin colada).
  // Cuando hay colada con obra, no se sobrescribe (esa relación está dada).
  // Sin colada o colada sin obra: usar la manual.
  const obraDeColada = coladaSel?.obra_works_no ?? null;
  const obraFinal = obraDeColada ?? obraManual;
  const obraDisplayColada = coladaSel?.obra_display_name ?? null;

  const valido = idActividad !== null && fechaColado && fcObjetivo > 0;

  const toggleEdad = (d: number) => {
    setEdadesSel((prev) => (prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d]));
  };

  const agregarEdadExtra = () => {
    const n = Number(edadExtra);
    if (Number.isInteger(n) && n > 0 && n <= 365 && !edadesSel.includes(n)) {
      setEdadesSel((prev) => [...prev, n].sort((a, b) => a - b));
      setEdadExtra('');
    }
  };

  const onSubmit = () => {
    if (!valido || idActividad === null) return;
    const body: CrearMuestraRequest = {
      obra_works_no: obraFinal,
      // id_casa queda como NULL en el flujo nuevo; el campo persiste en la
      // tabla para importar el Excel histórico (donde "Casa 12"/"Muros 2N"
      // eran texto libre antes de existir BC).
      id_casa: null,
      planta_nombre: plantaNombre.trim() || null,
      id_actividad: idActividad,
      fecha_colado: fechaColado,
      proveedor: 'ADELANTE DESARROLLOS',
      id_colada: idColada,
      id_receta_bc: null, // En el detalle se podrá vincular receta BC manual si hace falta
      fc_objetivo: fcObjetivo,
      categoria_concreto: categoria,
      tipo_concreto_libre: tipoLibre.trim() || null,
      notas: notas.trim() || null,
      edades_ensayos: edadesSel.slice().sort((a, b) => a - b),
    };
    crear.mutate(body, {
      onSuccess: (r) => {
        if (r.data) {
          props.cerrar();
          props.onCreado?.(r.data.id);
        }
      },
    });
  };

  const todasEdadesPicker = useMemo(
    () => Array.from(new Set([...EDADES_TIPICAS, ...EDADES_EXTRA])).sort((a, b) => a - b),
    [],
  );

  return (
    <Dialog
      open={props.abierto}
      onOpenChange={(o) => {
        if (!o && !crear.isPending) props.cerrar();
      }}
    >
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Nueva muestra de laboratorio</DialogTitle>
          <DialogDescription>
            Elegí la colada de la planta para autocompletar los datos. Si la muestra es de una
            planta no cubierta (ej. Fiori) saltá ese paso y llená manual.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-5">
          {/* ===== Paso 1: Colada ===== */}
          <section className="rounded-md border bg-muted/30 p-3">
            <div className="mb-2 text-sm font-semibold">
              1. Colada de la planta{' '}
              <span className="font-normal text-muted-foreground">(recomendado)</span>
            </div>
            <PickerColadas
              valor={idColada}
              onChange={(id, colada) => {
                setIdColada(id);
                setColadaSel(colada);
                if (id !== null) setMostrarManual(false);
              }}
              maxAltoPx={220}
            />
            {coladaSel && (
              <div className="mt-2 rounded-md border border-emerald-200 bg-emerald-50 p-2 text-xs text-emerald-900">
                <div className="font-medium">✓ Colada #{coladaSel.codigo_interno} seleccionada</div>
                <div className="mt-0.5 text-emerald-800">
                  Fecha y tipo de concreto autocompletados.
                  {obraDeColada ? (
                    <span className="ml-1">
                      Obra: <span className="font-mono">{obraDeColada}</span>
                      {obraDisplayColada && ` — ${obraDisplayColada}`}
                    </span>
                  ) : (
                    <span className="ml-1 italic">Esta colada no tiene obra asignada.</span>
                  )}
                </div>
              </div>
            )}
            {!coladaSel && !mostrarManual && (
              <button
                type="button"
                onClick={() => setMostrarManual(true)}
                className="mt-2 text-xs text-muted-foreground hover:text-foreground underline"
              >
                ¿Planta no cubierta (Fiori)? Llenar fecha manualmente sin colada
              </button>
            )}
          </section>

          {/* ===== Paso 2: Actividad + F'C (siempre visibles) ===== */}
          <section>
            <div className="mb-2 text-sm font-semibold">2. Datos básicos</div>

            {/* Actividad ocupa fila completa */}
            <div className="grid gap-1.5">
              <Label htmlFor="actividad">
                Actividad <span className="text-destructive">*</span>
              </Label>
              <Select
                value={idActividad === null ? '' : String(idActividad)}
                onValueChange={(v) => setIdActividad(Number(v))}
              >
                <SelectTrigger id="actividad">
                  <SelectValue placeholder="¿Qué se está colando?" />
                </SelectTrigger>
                <SelectContent>
                  {(actividades.data?.data?.actividades ?? []).map((a) => (
                    <SelectItem key={a.id} value={String(a.id)}>
                      {a.nombre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Obra: si la colada tiene obra, mostrarla; si no, picker manual.
               Solo un campo "Obra" porque en BC cada casa es su propia obra
               con su works_no — no hay sub-jerarquía. */}
            <div className="mt-3 grid gap-1.5">
              <Label>Obra</Label>
              {obraDeColada ? (
                <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm">
                  <span className="font-mono font-medium">{obraDeColada}</span>
                  {obraDisplayColada && (
                    <span className="ml-2 text-muted-foreground">— {obraDisplayColada}</span>
                  )}
                  <span className="ml-2 text-xs italic text-emerald-700">
                    (heredada de la colada)
                  </span>
                </div>
              ) : (
                <PickerObras valor={obraManual} onChange={setObraManual} maxAltoPx={180} />
              )}
            </div>

            {/* Planta: texto libre con atajos. Bianca/Roja son Blend (con colada);
               las Fiori (autohormigoneras) no pasan por coladas, así que este es
               el único lugar donde queda registrada su planta. */}
            <div className="mt-3 grid gap-1.5">
              <Label htmlFor="planta">Planta</Label>
              <div className="flex flex-wrap gap-1.5">
                {PLANTAS_LAB.map((p) => (
                  <Button
                    key={p}
                    type="button"
                    size="sm"
                    variant={plantaNombre === p ? 'default' : 'outline'}
                    onClick={() => setPlantaNombre(p)}
                    className="h-8 px-3 text-xs"
                  >
                    {p}
                  </Button>
                ))}
                <Input
                  id="planta"
                  value={plantaNombre}
                  onChange={(e) => setPlantaNombre(e.target.value)}
                  placeholder="Otra…"
                  className="h-8 w-32"
                  maxLength={50}
                />
              </div>
              {coladaSel && (
                <p className="text-xs text-muted-foreground">
                  Deducida de la colada. Ajustá si no corresponde.
                </p>
              )}
            </div>

            <div className="mt-3 grid gap-1.5">
              <Label>
                F'C objetivo (kg/cm²) <span className="text-destructive">*</span>
              </Label>
              <div className="flex flex-wrap gap-1.5">
                {FC_COMUNES.map((fc) => (
                  <Button
                    key={fc}
                    type="button"
                    size="sm"
                    variant={fcObjetivo === fc ? 'default' : 'outline'}
                    onClick={() => setFcObjetivo(fc)}
                    className="h-8 px-3 text-xs"
                  >
                    {fc}
                  </Button>
                ))}
                <Input
                  type="number"
                  value={fcObjetivo}
                  onChange={(e) => {
                    const n = Number(e.target.value);
                    if (Number.isFinite(n)) setFcObjetivo(n);
                  }}
                  className="h-8 w-24"
                  min={1}
                  max={2000}
                />
              </div>
              {coladaSel && (
                <p className="text-xs text-muted-foreground">
                  Deducido de la receta:{' '}
                  <span className="italic">
                    {coladaSel.descripcion_receta_bc ?? coladaSel.receta_blend_nombre}
                  </span>
                  . Ajustá si no corresponde.
                </p>
              )}
            </div>

            {/* Categoría: convencional vs autocompactable. Importante para
                el análisis posterior porque las curvas de fragua difieren. */}
            <div className="mt-3 grid gap-1.5">
              <Label>
                Tipo de concreto <span className="text-destructive">*</span>
              </Label>
              <div className="flex gap-1.5">
                <Button
                  type="button"
                  size="sm"
                  variant={categoria === 'convencional' ? 'default' : 'outline'}
                  onClick={() => setCategoria('convencional')}
                  className="h-9 px-4"
                >
                  Convencional
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={categoria === 'autocompactable' ? 'default' : 'outline'}
                  onClick={() => setCategoria('autocompactable')}
                  className="h-9 px-4"
                >
                  Autocompactable
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={categoria === 'otro' ? 'default' : 'outline'}
                  onClick={() => setCategoria('otro')}
                  className="h-9 px-4"
                >
                  Otro
                </Button>
              </div>
              {coladaSel && (
                <p className="text-xs text-muted-foreground">
                  Detectado de la receta. Ajustá si la planta usó algo distinto.
                </p>
              )}
            </div>
          </section>

          {/* ===== Paso 3: Fecha / Tipo (mostrar solo si NO hay colada o usuario quiere editar) ===== */}
          {(coladaSel === null || mostrarManual) && (
            <section>
              <div className="mb-2 text-sm font-semibold">
                {coladaSel ? 'Ajustar datos autocompletados' : '3. Fecha y tipo de concreto'}
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="grid gap-1.5">
                  <Label htmlFor="fecha-colado">
                    Fecha de colado <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="fecha-colado"
                    type="date"
                    value={fechaColado}
                    onChange={(e) => setFechaColado(e.target.value)}
                  />
                </div>
                <div className="grid gap-1.5">
                  <Label htmlFor="tipo-libre">Tipo de concreto</Label>
                  <Input
                    id="tipo-libre"
                    value={tipoLibre}
                    onChange={(e) => setTipoLibre(e.target.value)}
                    placeholder='Ej: "210 LOSA", "SELLO 280"'
                  />
                </div>
              </div>
            </section>
          )}

          {/* ===== Paso 4: Edades de prueba ===== */}
          <section>
            <div className="mb-1 text-sm font-semibold">
              {coladaSel ? '3' : '4'}. Edades de prueba
            </div>
            <p className="mb-2 text-xs text-muted-foreground">
              Por cada edad seleccionada se crea un ensayo listo para recibir 2 cilindros (típico).
              Podés agregar más cilindros después si hace falta.
            </p>
            <div className="flex flex-wrap gap-1.5">
              {todasEdadesPicker.map((d) => {
                const seleccionada = edadesSel.includes(d);
                const esTipica = (EDADES_TIPICAS as readonly number[]).includes(d);
                return (
                  <Button
                    key={d}
                    type="button"
                    size="sm"
                    variant={seleccionada ? 'default' : 'outline'}
                    onClick={() => toggleEdad(d)}
                    className={cn(
                      'h-8 px-3 text-xs',
                      seleccionada && esTipica && 'ring-2 ring-emerald-500/40',
                    )}
                  >
                    {seleccionada && <Check className="mr-1 h-3 w-3" />}
                    {d} días
                  </Button>
                );
              })}
              <div className="flex items-center gap-1">
                <Input
                  type="number"
                  placeholder="Otra"
                  value={edadExtra}
                  onChange={(e) => setEdadExtra(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && agregarEdadExtra()}
                  className="h-8 w-20 text-xs"
                  min={1}
                  max={365}
                />
                <Button
                  type="button"
                  size="icon"
                  variant="outline"
                  className="h-8 w-8"
                  onClick={agregarEdadExtra}
                  disabled={!edadExtra}
                >
                  <Plus className="h-3 w-3" />
                </Button>
              </div>
            </div>
            {edadesSel.length === 0 && (
              <p className="mt-1 text-xs text-amber-700">
                Sin edades seleccionadas: la muestra se crea sin ensayos. Podés agregarlos después
                desde el detalle.
              </p>
            )}
          </section>

          {/* ===== Notas (opcional, plegable) ===== */}
          <section>
            <Label htmlFor="notas" className="text-xs text-muted-foreground">
              Notas (opcional)
            </Label>
            <textarea
              id="notas"
              value={notas}
              onChange={(e) => setNotas(e.target.value)}
              className="mt-1 min-h-[40px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              placeholder="Observaciones del muestreo…"
            />
          </section>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={props.cerrar} disabled={crear.isPending}>
            <X className="h-4 w-4" /> Cancelar
          </Button>
          <Button
            type="button"
            onClick={onSubmit}
            disabled={!valido || crear.isPending}
            className="gap-1.5"
          >
            <Building2 className="h-4 w-4" />
            {crear.isPending
              ? 'Creando…'
              : edadesSel.length > 0
                ? `Crear muestra + ${edadesSel.length} ensayo${edadesSel.length === 1 ? '' : 's'}`
                : 'Crear muestra'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
